import React, { useEffect, useMemo, useState } from "react";
import { api, roleLabel } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import { ShieldCheck, RotateCcw, Save, Loader2, Users } from "lucide-react";

const inputCls =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition";

// Static localized labels for the module + action headers of the matrix.
const MODULE_LABELS = {
  dashboard: { en: "Dashboard", pt: "Dashboard" },
  tickets: { en: "Tickets", pt: "Chamados" },
  projects: { en: "Projects", pt: "Projetos" },
  estimates: { en: "Estimates", pt: "Orçamentos" },
  invoices: { en: "Invoices", pt: "Faturas" },
  payroll: { en: "Payroll", pt: "Folha" },
  timesheets: { en: "Timesheets", pt: "Ponto" },
  crew: { en: "Crew on the field", pt: "Equipe em campo" },
  hr: { en: "HR Management", pt: "RH" },
  materials: { en: "Materials", pt: "Materiais" },
  material_trends: { en: "Material Trends", pt: "Tendências de Preços" },
  purchase_orders: { en: "Purchase Orders", pt: "POs" },
  suppliers: { en: "Suppliers", pt: "Fornecedores" },
  equipment: { en: "Equipment / Vehicles", pt: "Equipamentos / Veículos" },
  subcontractors: { en: "Subcontractors", pt: "Subempreiteiros" },
  financials: { en: "Financials", pt: "Financeiro" },
  properties: { en: "Properties", pt: "Propriedades" },
  clients: { en: "Clients", pt: "Clientes" },
  users: { en: "Users", pt: "Usuários" },
  reports: { en: "Reports", pt: "Relatórios" },
  settings: { en: "Settings", pt: "Configurações" },
};

const ACTION_LABELS = {
  view: { en: "View", pt: "Ver" },
  create: { en: "Create", pt: "Criar" },
  edit: { en: "Edit", pt: "Editar" },
  delete: { en: "Delete", pt: "Excluir" },
  approve: { en: "Approve", pt: "Aprovar" },
};

const ACTION_COLORS = {
  view: "bg-slate-100 text-slate-700 hover:bg-slate-200",
  create: "bg-emerald-50 text-emerald-700 hover:bg-emerald-100",
  edit: "bg-amber-50 text-amber-700 hover:bg-amber-100",
  delete: "bg-rose-50 text-rose-700 hover:bg-rose-100",
  approve: "bg-blue-50 text-blue-700 hover:bg-blue-100",
};

export default function PermissionsPage() {
  const { t, lang } = useLang();
  const [catalog, setCatalog] = useState(null); // { modules, actions, roles, role_labels }
  const [templates, setTemplates] = useState({}); // { role: { permissions } }
  const [activeRole, setActiveRole] = useState("admin");
  const [draft, setDraft] = useState({}); // { module: [actions] }
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [cat, tpl] = await Promise.all([
        api.get("/permissions/modules"),
        api.get("/permissions/templates"),
      ]);
      setCatalog(cat);
      setTemplates(tpl);
      const initialRole = activeRole || cat.roles[0];
      setDraft({ ...(tpl[initialRole]?.permissions || {}) });
      setActiveRole(initialRole);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    /* eslint-disable-next-line */
  }, []);

  const switchRole = (role) => {
    setActiveRole(role);
    setDraft({ ...(templates[role]?.permissions || {}) });
  };

  const isDirty = useMemo(() => {
    const orig = JSON.stringify(templates[activeRole]?.permissions || {});
    return orig !== JSON.stringify(draft || {});
  }, [templates, activeRole, draft]);

  const toggle = (mod, action) => {
    setDraft((prev) => {
      const acts = prev[mod] ? [...prev[mod]] : [];
      const idx = acts.indexOf(action);
      if (idx >= 0) acts.splice(idx, 1);
      else acts.push(action);
      const next = { ...prev };
      if (acts.length) next[mod] = acts;
      else delete next[mod];
      return next;
    });
  };

  const toggleRow = (mod, allActions) => {
    setDraft((prev) => {
      const current = prev[mod] || [];
      const hasAll = allActions.every((a) => current.includes(a));
      const next = { ...prev };
      if (hasAll) delete next[mod];
      else next[mod] = [...allActions];
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/permissions/templates/${activeRole}`, { permissions: draft });
      toast.success(t("saved") || "Saved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error"));
    } finally {
      setBusy(false);
    }
  };

  const resetToDefault = async () => {
    if (!window.confirm(t("permReset") || "Reset this role to the shipped defaults?")) return;
    setBusy(true);
    try {
      await api.post(`/permissions/templates/${activeRole}/reset`);
      toast.success(t("saved") || "Reset");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error"));
    } finally {
      setBusy(false);
    }
  };

  if (loading || !catalog) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const modLabel = (m) => MODULE_LABELS[m]?.[lang] || m;
  const actLabel = (a) => ACTION_LABELS[a]?.[lang] || a;

  return (
    <div className="space-y-6" data-testid="permissions-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400">
            <ShieldCheck className="w-4 h-4" /> {t("permissions") || "Permissions"}
          </div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 mt-1">
            {t("permissionsTitle") || "Role & Permission Templates"}
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            {t("permissionsSubtitle") ||
              "Fine-tune what each role can view, create, edit, delete or approve. Users inherit these defaults unless you set a per-user override."}
          </p>
        </div>
      </div>

      {/* Role tabs */}
      <div className="flex flex-wrap gap-2" data-testid="perm-role-tabs">
        {catalog.roles.map((r) => {
          const active = r === activeRole;
          return (
            <button
              key={r}
              data-testid={`perm-role-tab-${r}`}
              onClick={() => switchRole(r)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                active
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              }`}
            >
              {roleLabel(r, lang)}
              <span className={`ml-1.5 text-[10px] ${active ? "text-slate-300" : "text-slate-400"}`}>({r})</span>
            </button>
          );
        })}
      </div>

      {/* Matrix */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-900">{roleLabel(activeRole, lang)}</span>
            <span className="text-xs text-slate-400">— {templates[activeRole]?.system_default
              ? (t("systemDefault") || "system default") : (t("customized") || "customized")}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="perm-reset-btn"
              onClick={resetToDefault}
              disabled={busy}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 border border-slate-300 rounded-lg px-2.5 py-1.5 hover:bg-slate-100 disabled:opacity-40"
            >
              <RotateCcw className="w-3.5 h-3.5" /> {t("resetToDefault") || "Reset to default"}
            </button>
            <button
              data-testid="perm-save-btn"
              onClick={save}
              disabled={busy || !isDirty}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-slate-900 rounded-lg px-3 py-1.5 hover:bg-slate-800 disabled:opacity-40"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {t("save") || "Save"}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase font-bold tracking-wider text-slate-400 border-b border-slate-200 bg-slate-50/60">
                <th className="text-left py-2.5 px-4 w-56">{t("module") || "Module"}</th>
                {catalog.actions.map((a) => (
                  <th key={a} className="py-2.5 px-3 text-center">
                    {actLabel(a)}
                  </th>
                ))}
                <th className="py-2.5 px-3 text-right pr-4">{t("all") || "All"}</th>
              </tr>
            </thead>
            <tbody>
              {catalog.modules.map((m) => {
                const rowActs = draft[m] || [];
                const allOn = catalog.actions.every((a) => rowActs.includes(a));
                return (
                  <tr key={m} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/50">
                    <td className="py-2.5 px-4 font-medium text-slate-800">{modLabel(m)}</td>
                    {catalog.actions.map((a) => {
                      const on = rowActs.includes(a);
                      return (
                        <td key={a} className="py-2 px-3 text-center">
                          <button
                            data-testid={`perm-cell-${m}-${a}`}
                            onClick={() => toggle(m, a)}
                            className={`inline-flex items-center justify-center w-8 h-8 rounded-md border transition-all ${
                              on
                                ? `${ACTION_COLORS[a]} border-transparent shadow-sm`
                                : "bg-white text-slate-300 border-slate-200 hover:border-slate-400"
                            }`}
                            title={`${modLabel(m)} — ${actLabel(a)}`}
                          >
                            {on ? "✓" : ""}
                          </button>
                        </td>
                      );
                    })}
                    <td className="py-2 px-3 text-right pr-4">
                      <button
                        data-testid={`perm-row-all-${m}`}
                        onClick={() => toggleRow(m, catalog.actions)}
                        className={`text-[11px] font-semibold rounded-md px-2 py-1 border transition ${
                          allOn
                            ? "bg-slate-900 text-white border-slate-900"
                            : "bg-white text-slate-500 border-slate-200 hover:bg-slate-100"
                        }`}
                      >
                        {allOn ? (t("clear") || "Clear") : (t("selectAll") || "All")}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50/60 flex items-center gap-3 text-[11px] text-slate-500">
          <Users className="w-3.5 h-3.5" />
          {t("permsFooterHint") ||
            "Users of this role inherit these permissions automatically. Custom per-user overrides can be set from the user detail page."}
        </div>
      </div>
    </div>
  );
}
